import React, { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import ReactPlayer from "react-player";
import { SelectedVideo, Module } from "./types";
import { extractYoutubeId } from "./utils";
import { toast } from "react-toastify";
import {
  updateCourseProgress,
  fetchCourseProgress,
  addCourseYoutubeMapping,
  loadSavedYoutubeMappings,
} from "./api";

interface VideoPlayerProps {
  selectedVideo: SelectedVideo | null;
  modules: Module[];
  closeVideo: () => void;
  onProgressUpdate: () => void;
}

interface ProgressState {
  currentTime: number;
  duration: number;
  percent: number;
  completed: boolean;
  lastSavedPercent: number;
  lastSavedTime: number;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  selectedVideo,
  modules,
  closeVideo,
  onProgressUpdate,
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [youtubeIdInput, setYoutubeIdInput] = useState("");
  const [videoStarted, setVideoStarted] = useState(false);
  const [progressState, setProgressState] = useState<ProgressState>({
    currentTime: 0,
    duration: 0,
    percent: 0,
    completed: false,
    lastSavedPercent: 0,
    lastSavedTime: 0,
  });

  // Interval reference stored outside of useState to ensure clean removal
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Sauvegarder la progression actuelle
  const saveCurrentProgress = useCallback(() => {
    if (selectedVideo && videoStarted && progressState.duration > 0) {
      const { percent, completed } = progressState;

      console.log(
        `Sauvegarde de la progression: ${percent}%${
          completed ? " (complété)" : ""
        }`
      );

      updateCourseProgress(selectedVideo.courseId, percent, completed)
        .then(() => {
          onProgressUpdate();
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la sauvegarde de la progression:",
            error
          );
        });
    }
  }, [selectedVideo, videoStarted, progressState, onProgressUpdate]);

  // Arrêter le suivi de progression
  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  // Charger les mappages sauvegardés au démarrage
  useEffect(() => {
    loadSavedYoutubeMappings();

    // Cleanup function for component unmount
    return () => {
      stopProgressTracking();
      saveCurrentProgress();
    };
  }, [saveCurrentProgress]);

  // Démarrer le suivi de progression
  const startProgressTracking = () => {
    if (progressIntervalRef.current) return; // Éviter les doublons

    progressIntervalRef.current = setInterval(() => {
      if (!playerRef.current) return;

      const currentTime = playerRef.current.getCurrentTime() || 0;
      const duration = playerRef.current.getDuration() || 0;

      if (duration <= 0) return;

      const newPercent = Math.floor((currentTime / duration) * 100);
      const isCompleted = newPercent >= 95;

      // Mettre à jour l'état local avec les nouvelles valeurs
      setProgressState((prevState) => {
        const newState = {
          ...prevState,
          currentTime,
          duration,
          percent: newPercent,
          completed: isCompleted,
        };

        // Déterminer si nous devons sauvegarder la progression
        const shouldSave =
          // Toutes les 10% de progression (mais pas les mêmes 10% plusieurs fois)
          (newPercent % 10 === 0 &&
            newPercent > 0 &&
            newPercent !== prevState.lastSavedPercent) ||
          // Toutes les 30 secondes
          (Math.floor(currentTime) > 0 &&
            Math.floor(currentTime) % 30 === 0 &&
            Math.floor(currentTime) !== Math.floor(prevState.lastSavedTime)) ||
          // Quand la vidéo est complétée (95% ou plus)
          (isCompleted && !prevState.completed);

        // Sauvegarder la progression si nécessaire
        if (shouldSave && selectedVideo) {
          console.log(
            `Mise à jour de la progression à ${newPercent}%${
              isCompleted ? " (complété)" : ""
            }`
          );

          updateCourseProgress(selectedVideo.courseId, newPercent, isCompleted)
            .then(() => {
              onProgressUpdate();
            })
            .catch((error) => {
              console.error(
                "Erreur lors de la mise à jour de la progression:",
                error
              );
            });

          // Mettre à jour les valeurs sauvegardées
          newState.lastSavedPercent = newPercent;
          newState.lastSavedTime = currentTime;
        }

        return newState;
      });
    }, 3000);
  };

  // Ajouter un écouteur de clavier pour le raccourci admin (Alt+Shift+Y)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.shiftKey && event.key === "Y") {
        setIsAdminPanelOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Gérer la soumission du formulaire admin
  const handleAdminSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedVideo && youtubeIdInput) {
      addCourseYoutubeMapping(String(selectedVideo.courseId), youtubeIdInput);
      setYoutubeIdInput("");
      setIsAdminPanelOpen(false);
      // Forcer un rafraîchissement de la page pour appliquer le nouveau mappage
      window.location.reload();
    }
  };

  // Effet pour charger la progression initiale quand la vidéo est sélectionnée
  useEffect(() => {
    if (selectedVideo) {
      console.log("Initialisation du lecteur vidéo pour:", {
        courseId: selectedVideo.courseId,
        title: selectedVideo.title,
        module: selectedVideo.module,
        order: selectedVideo.order,
      });

      // Nettoyer l'intervalle existant si besoin
      stopProgressTracking();

      // Réinitialiser l'état de progression pour la nouvelle vidéo
      setProgressState({
        currentTime: 0,
        duration: 0,
        percent: 0,
        completed: false,
        lastSavedPercent: 0,
        lastSavedTime: 0,
      });

      // Réinitialiser l'état de démarrage de la vidéo
      setVideoStarted(false);
    }
  }, [selectedVideo]);

  // Gérer la lecture de la vidéo
  const handlePlay = () => {
    setVideoStarted(true);
    startProgressTracking();
  };

  // Gérer la mise en pause de la vidéo
  const handlePause = () => {
    if (videoStarted && selectedVideo) {
      // Sauvegarder la progression actuelle à chaque pause
      saveCurrentProgress();
    }
  };

  // Gérer la fin de la vidéo
  const handleEnded = () => {
    if (videoStarted && selectedVideo) {
      console.log("Vidéo terminée, marquage comme complété");

      // Arrêter le suivi
      stopProgressTracking();

      // S'assurer que la vidéo est marquée comme 100% complétée
      updateCourseProgress(selectedVideo.courseId, 100, true)
        .then(() => {
          console.log("Progression mise à jour avec succès après fin de vidéo");
          onProgressUpdate();

          // Mettre à jour l'état local
          setProgressState((prev) => ({
            ...prev,
            percent: 100,
            completed: true,
            lastSavedPercent: 100,
          }));

          // Informer l'utilisateur que la vidéo est complétée
          try {
            toast.success("Félicitations ! Vous avez terminé cette capsule.");
          } catch (err) {
            console.error("Erreur lors de l'affichage du toast:", err);
          }
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la mise à jour de la progression après fin de vidéo:",
            error
          );
        });
    }
  };

  // Gérer le chargement initial de la vidéo
  const handleReady = () => {
    if (selectedVideo && playerRef.current) {
      // Charger la progression existante depuis l'API
      fetchCourseProgress(selectedVideo.courseId)
        .then((existingProgress) => {
          if (existingProgress && playerRef.current) {
            console.log("Progression existante chargée:", existingProgress);

            // Mettre à jour l'état de progression avec les données existantes
            setProgressState((prev) => ({
              ...prev,
              percent: existingProgress.progression,
              completed: existingProgress.completed,
              lastSavedPercent: existingProgress.progression,
            }));

            // Si une progression existe et n'est pas complétée, essayer de reprendre là où l'utilisateur s'était arrêté
            if (
              existingProgress.progression > 0 &&
              existingProgress.progression < 95 &&
              !existingProgress.completed
            ) {
              const duration = playerRef.current.getDuration();
              if (duration > 0) {
                const timeToSeek =
                  (existingProgress.progression / 100) * duration;
                // Seek to the saved position
                playerRef.current.seekTo(timeToSeek, "seconds");
                console.log(
                  `Reprise de la lecture à ${timeToSeek.toFixed(1)} secondes (${
                    existingProgress.progression
                  }%)`
                );
              }
            }
          }
        })
        .catch((error) => {
          console.error("Erreur lors du chargement de la progression:", error);
        });
    }
  };

  // Fonction pour obtenir l'URL de la vidéo
  const getVideoUrl = (skipLogging = false): string => {
    try {
      if (!selectedVideo) {
        if (!skipLogging) console.log("selectedVideo est null");
        return "";
      }

      // 1. Vérifier si l'URL vidéo est directement fournie dans le selectedVideo
      if (selectedVideo.videoUrl) {
        if (!skipLogging)
          console.log(
            "Utilisation de l'URL vidéo fournie dans le payload:",
            selectedVideo.videoUrl
          );

        return selectedVideo.videoUrl;
      }

      // 2. Rechercher dans les modules pour trouver le cours avec son URL vidéo
      if (Array.isArray(modules) && modules.length > 0) {
        if (!skipLogging) {
          console.log(
            "Recherche du cours dans les modules:",
            selectedVideo.courseId
          );
        }

        const course = modules
          .flatMap((m) => m.courses || [])
          .find((c) => c && c.id === selectedVideo.courseId);

        if (course) {
          if (!skipLogging) {
            console.log("Cours trouvé dans les modules:", {
              id: course.id,
              title: course.title,
              videoUrl: course.videoUrl || "Non définie",
            });
          }

          if (course.videoUrl) {
            if (!skipLogging)
              console.log("URL depuis le cours:", course.videoUrl);
            return course.videoUrl;
          }
        }
      }

      // 3. Utiliser l'ID du cours avec la fonction extractYoutubeId (mappages hardcodés)
      const courseIdStr = String(selectedVideo.courseId);
      const videoId = extractYoutubeId(courseIdStr);

      if (videoId) {
        const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
        if (!skipLogging)
          console.log("URL YouTube depuis ID mappé:", youtubeUrl);
        return youtubeUrl;
      }

      if (!skipLogging) console.log("Aucune URL de vidéo n'a pu être générée");
      return "";
    } catch (error) {
      console.error(
        "Erreur lors de la génération de l'URL de la vidéo:",
        error
      );
      return "";
    }
  };

  if (!selectedVideo) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl transform transition-all duration-300 animate-fadeIn">
        {/* En-tête du modal */}
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              {selectedVideo.title}
            </h3>
            <p className="text-orange-500 text-sm font-medium">
              {selectedVideo.module} • Capsule {selectedVideo.order}
            </p>
          </div>
          <button
            onClick={() => {
              // Sauvegarder la progression avant de fermer
              saveCurrentProgress();
              stopProgressTracking();
              closeVideo();
            }}
            className="text-gray-500 hover:text-orange-500 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors duration-300 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>

        {/* Corps du modal avec la vidéo */}
        <div className="flex-grow overflow-auto p-4">
          {/* Panneau d'administration (visible uniquement avec le raccourci) */}
          {isAdminPanelOpen && (
            <div className="mb-4 p-3 bg-gray-100 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Mode Admin - Mappage d'ID YouTube pour le cours{" "}
                {selectedVideo.courseId}
              </h4>
              <form onSubmit={handleAdminSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={youtubeIdInput}
                  onChange={(e) => setYoutubeIdInput(e.target.value)}
                  placeholder="ID YouTube (11 caractères)"
                  className="flex-1 p-1 text-sm border rounded"
                  maxLength={11}
                />
                <button
                  type="submit"
                  className="px-3 py-1 bg-orange-500 text-white text-sm rounded"
                >
                  Appliquer
                </button>
              </form>
              <p className="text-xs text-gray-500 mt-1">
                Entrez l'ID YouTube à associer à ce cours.
              </p>
            </div>
          )}

          {/* Indicateur de progression (optionnel) */}
          {videoStarted && progressState.percent > 0 && (
            <div className="mb-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${
                    progressState.completed ? "bg-green-500" : "bg-orange-500"
                  }`}
                  style={{ width: `${progressState.percent}%` }}
                ></div>
              </div>
            </div>
          )}

          <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg">
            {getVideoUrl() ? (
              <ReactPlayer
                ref={playerRef}
                url={getVideoUrl()}
                width="100%"
                height="100%"
                controls={true}
                playing={false}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onReady={handleReady}
                progressInterval={1000}
                config={{
                  youtube: {
                    playerVars: {
                      rel: 0,
                      modestbranding: 1,
                    },
                  },
                }}
                className="react-player"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800">
                <p className="text-white mb-4">URL de vidéo non disponible</p>
                <p className="text-gray-400 text-sm">
                  Appuyez sur Alt+Shift+Y pour accéder au mode admin et ajouter
                  manuellement l'ID YouTube.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
